import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clientApi,
  type Client,
  type CreateClientData,
  type UpdateClientData,
} from '../api/clientApi';
import { billingStatusKey } from './useBilling';

// Query keys for React Query cache management
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (filters: string) => [...clientKeys.lists(), { filters }] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
};

// Hook to fetch all clients
export const useClients = () => {
  return useQuery({
    queryKey: clientKeys.lists(),
    queryFn: clientApi.getAllClients,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

// Hook to fetch a single client by ID
export const useClient = (id: string) => {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientApi.getClientById(id),
    enabled: !!id, // Only run if id is provided
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to create a new client
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientApi.createClient,
    onSuccess: (newClient: Client) => {
      // Invalidate and refetch clients list
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });

      // Optionally, add the new client to the cache
      queryClient.setQueryData(clientKeys.detail(newClient._id), newClient);
    },
    onError: error => {
      console.error('Failed to create client:', error);
    },
  });
};

// Hook to update an existing client
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientData }) =>
      clientApi.updateClient(id, data),
    onSuccess: (updatedClient: Client) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientKeys.detail(updatedClient._id), updatedClient);

      // Invalidate clients list to ensure consistency
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
    onError: error => {
      console.error('Failed to update client:', error);
    },
  });
};

// Hook to delete a client
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientApi.deleteClient,
    onSuccess: (_, deletedId: string) => {
      // Remove the client from cache
      queryClient.removeQueries({ queryKey: clientKeys.detail(deletedId) });

      // Invalidate clients list
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });
    },
    onError: error => {
      console.error('Failed to delete client:', error);
    },
  });
};

// Hook to add a task to a client
export const useAddTaskToClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, taskId }: { clientId: string; taskId: string }) =>
      clientApi.addTaskToClient(clientId, taskId),
    onSuccess: (updatedClient: Client) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientKeys.detail(updatedClient._id), updatedClient);

      // Invalidate clients list to ensure consistency
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });

      // You might also want to invalidate task queries if you have them
      // queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: error => {
      console.error('Failed to add task to client:', error);
    },
  });
};

// Hook to remove a task from a client
export const useRemoveTaskFromClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, taskId }: { clientId: string; taskId: string }) =>
      clientApi.removeTaskFromClient(clientId, taskId),
    onSuccess: (updatedClient: Client) => {
      // Update the specific client in cache
      queryClient.setQueryData(clientKeys.detail(updatedClient._id), updatedClient);

      // Invalidate clients list to ensure consistency
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
    onError: error => {
      console.error('Failed to remove task from client:', error);
    },
  });
};

// Hook for optimistic updates when creating a client
export const useCreateClientOptimistic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientApi.createClient,
    onMutate: async (newClient: CreateClientData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: clientKeys.lists() });

      // Snapshot the previous value
      const previousClients = queryClient.getQueryData(clientKeys.lists());

      // Optimistically update to the new value
      const optimisticClient: Client = {
        _id: `temp-${Date.now()}`, // Temporary ID
        ...newClient,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        project: [],
      };

      queryClient.setQueryData(clientKeys.lists(), (old: Client[] | undefined) =>
        old ? [...old, optimisticClient] : [optimisticClient]
      );

      // Return a context object with the snapshotted value
      return { previousClients };
    },
    onError: (err, newClient, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(clientKeys.lists(), context?.previousClients);
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
};
